//Enhanced Logic.js with AI Integration
//Based on original Logic.js but adds AI analysis capabilities

import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

// Helper functions (keeping original logic)
const extractGrade = (name) => {
  if (!name) return null;
  const gradeMatch = name.match(/\[(.*?)\]/);
  if (!gradeMatch) return null;

  const grade = gradeMatch[1];
  if (grade === 'Brand New') return 'BN';
  if (grade === 'Grade A') return 'GA';
  if (grade === 'Grade B') return 'GB';
  if (grade === 'Open Box') return 'OB';
  if (grade === 'Like New') return 'LN';
  return null;
};

const extractStorage = (name) => {
  if (!name || typeof name !== 'string') return null;

  // Check for storage in parentheses with space (multiple storage)
  const multiStorageMatch = name.match(/\((\d+(?:GB|TB)?\s+\d+(?:GB|TB)?)\)/i);
  if (multiStorageMatch) {
    const storageStr = multiStorageMatch[1];
    const sizes = storageStr.split(/\s+/)
      .map(s => {
        const match = s.match(/(\d+)(?:GB|TB)?/i);
        return match ? parseInt(match[1]) : null;
      })
      .filter(s => s !== null);
    
    return {
      sizes,
      isMultiple: true,
      originalMultiStorage: multiStorageMatch[1]
    };
  }

  // Single storage in parentheses or without
  const storageMatch = name.match(/(?:\()?(\d+)\s*(GB|TB)(?:\))?/i);
  if (storageMatch) {
    return {
      size: parseInt(storageMatch[1]),
      unit: storageMatch[2].toUpperCase(),
      isMultiple: false
    };
  }
  // Check for common storage values (128, 256, 512) without GB/TB unit
  const commonStorageMatch = name.match(/\s(64|128|256|512)\s/);
  if (commonStorageMatch) {
    return {
      size: parseInt(commonStorageMatch[1]),
      unit: "GB", // Assume GB for these common values
      isMultiple: false
    };
  }
  return null;
};

const normalizeProductName = (name) => {
  if (!name || typeof name !== 'string') return { normalized: '', storage: null };
  
  // Extract storage before normalization
  const storage = extractStorage(name);
  
  let cleaned = name;
  
  // Only remove text between " -" and parenthesis if both patterns exist
  if (cleaned.includes(' -') && cleaned.includes('(')) {
    const dashIndex = cleaned.indexOf(' -');
    const parenIndex = cleaned.indexOf('(', dashIndex);
    if (parenIndex > dashIndex) {
      cleaned = cleaned.substring(0, dashIndex) + ' ' + cleaned.substring(parenIndex);
    }
  }
  
  cleaned = cleaned
    .replace(/5G\s+/g, '') // Remove 5G with trailing space
    .replace(/\[[^\]]*\]/g, '') // Remove grade in square brackets
    .replace(/\((\d+)\s*GB\)/gi, '$1') // Keep numbers, remove (GB) from parentheses
    .replace(/(\d+)\s*GB/gi, '$1') // Keep numbers, remove GB without parentheses
    .replace(/\((\d+)\s*TB\)/gi, '$1TB') // Keep numbers with TB unit
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing spaces
  return {
    normalized: cleaned,
    storage
  };
};

const getBaseProductName = (name) => {
  if (!name) return '';
  return name
    .replace(/\s*\[[^\]]*\]\s*/, '') // Remove grade in brackets
    .replace(/\s*\(\d+(?:GB)?\)\s*/i, '') // Remove storage in parentheses
    .replace(/\s*\([A-Z0-9,]+\)\s*/i, '') // Remove model numbers in parentheses
    .trim();
};

const cleanIngramProduct = (product) => {
  if (!product) return '';
  const parts = product.split(' -');
  return parts[0].trim();
};

// NEW: Extract standardized name from matchedProducts
// Always take the first actual product name (before the first pipe OR first newline),
// remove headings like "OC Products:" / "Matching Ingram Products:", grades and network tags.
// Example: "Apple iPhone 12 Mini 5G (64GB) [Brand New] | ..." → "Apple iPhone 12 Mini 64GB"
const extractStandardizedNameFromMatchedProducts = (matchedProductsString) => {
  if (!matchedProductsString || typeof matchedProductsString !== 'string') return '';

  // Normalize line endings and remove headings
  let normalized = matchedProductsString
    .replace(/\r\n?/g, '\n')
    .replace(/OC Products:\s*/gi, '')
    .replace(/Matching Ingram Products:\s*/gi, '');

  // Split by pipe or newline and pick the first non-empty token
  const tokens = normalized
    .split(/\s*\|\s*|\n+/)
    .map(t => t.trim())
    .filter(t => t.length > 0);

  const firstProduct = tokens.length ? tokens[0] : '';
  if (!firstProduct) return '';

  let cleaned = firstProduct;

  // Remove grade indicators in square brackets
  cleaned = cleaned.replace(/\s*\[[^\]]*\]\s*/g, '');

  // Remove network indicators (5G, 4G, LTE, WiFi, Cellular)
  cleaned = cleaned.replace(/\b(5G|4G|LTE|WiFi|Cellular)\b/gi, '');

  // Normalize storage like (128GB) → 128GB, (1TB) → 1TB
  cleaned = cleaned.replace(/\((\d+)\s*GB\)/gi, '$1GB');
  cleaned = cleaned.replace(/\((\d+)\s*TB\)/gi, '$1TB');

  // Clean up extra spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
};

const getNameVariations = (normalizedName) => {
  const variations = [normalizedName];
  
  // If name contains "Plus", add version with "+"
  if (normalizedName.includes(' Plus')) {
    variations.push(normalizedName.replace(' Plus', '+'));
  }
  // If name contains "+", add version with "Plus"
  else if (normalizedName.includes('+')) {
    variations.push(normalizedName.replace('+', ' Plus'));
  }

  // Handle SE 3rd Gen variation
  if (normalizedName.includes(' SE ')) {
    const seMatch = normalizedName.match(/SE (\d)(?:rd|th|st|nd) Gen/i);
    if (seMatch) {
      const seNumber = seMatch[1];
      const seVariation = normalizedName.replace(/SE \d(?:rd|th|st|nd) Gen/i, `SE${seNumber}`);
      variations.push(seVariation);
    }
  }
  
  // Handle SE2 to SE 2020 matching (and vice versa)
  if (normalizedName.includes(' SE2 ')) {
    console.log('SE2 match found:', normalizedName);
    variations.push(normalizedName.replace(' SE2 ', ' SE 2020 '));
  } else if (normalizedName.includes(' SE 2020 ')) {
    console.log('SE 2020 match found:', normalizedName);
    variations.push(normalizedName.replace(' SE 2020 ', ' SE2 '));
  }

  // Handle Pixel Pro variation (only for Pixel products)
  if (normalizedName.includes(' Pixel ')) {
    const pixelMatch = normalizedName.match(/Pixel (\d+) Pro/i);
    if (pixelMatch) {
      const pixelNumber = pixelMatch[1];
      const pixelVariation = normalizedName.replace(`Pixel ${pixelNumber} Pro`, `Pixel ${pixelNumber}Pro`);
      variations.push(pixelVariation);
    }
  }
  
  return variations;
};

const processModelMatches = (ocProducts, ingramData) => {
  const results = [];
  const modelMatches = new Map(); // model -> { storage -> products }
  const nameMatches = new Map();
  const processedIngramIndices = new Set();

  // First organize OC products by normalized name
  const normalizedOcProducts = new Map(); // normalized name -> product info

  ocProducts.forEach(ocProduct => {
    if (!ocProduct.Name) return;
    
    const { normalized: normalizedName, storage } = normalizeProductName(ocProduct.Name);
    const grade = extractGrade(ocProduct.Name);
    const quantity = parseInt(ocProduct.Qty) || 0;

    if (!normalizedOcProducts.has(normalizedName)) {
      normalizedOcProducts.set(normalizedName, {
        name: normalizedName,
        storage: storage,
        grades: [],
        quantities: { BN: 0, GB: 0, GA: 0, OB: 0, LN: 0 },
        originalProducts: []
      });
    }

    const productInfo = normalizedOcProducts.get(normalizedName);
    if (grade) {
      productInfo.grades.push(grade);
      productInfo.quantities[grade] += quantity;
    }
    productInfo.originalProducts.push(ocProduct);
  });

  // First collect all valid models from OC products
  ocProducts.forEach(ocProduct => {
    if (!ocProduct.Model || 
        ocProduct.Model.length <= 3 || 
        String(ocProduct.Model).includes(' ')) {
      return;
    }

    if (!modelMatches.has(ocProduct.Model)) {
      modelMatches.set(ocProduct.Model, new Map()); // storage -> products map
    }
    
    // Get storage info
    const storage = extractStorage(ocProduct.Name);
    const storageKey = storage 
      ? (storage.isMultiple 
          ? storage.sizes.join(',')
          : (storage.unit === 'TB' ? `${storage.size}${storage.unit}` : `${storage.size}`))
      : 'NO_STORAGE';
    
    if (!modelMatches.get(ocProduct.Model).has(storageKey)) {
      modelMatches.get(ocProduct.Model).set(storageKey, {
        products: [],
        ingramIndices: new Set()
      });
    }
    
    modelMatches.get(ocProduct.Model).get(storageKey).products.push(ocProduct);
  });

  // Process each Ingram product
  ingramData.forEach((ingramProduct, index) => {
    const cleanedProduct = cleanIngramProduct(ingramProduct.Product);
    const ingramStorage = extractStorage(ingramProduct.Product);
    const firstWord = cleanedProduct.split(' ')[0];
    
    modelMatches.forEach((storageMap, model) => {
      if (model === firstWord) return;
      
      const modelStr = String(model);
      const withoutDsORDsn = modelStr.replace(/\/DSN?$/, '');
      const modelExists = cleanedProduct.includes(modelStr) || 
        (modelStr.startsWith('SM-') && cleanedProduct.includes(modelStr + '/DS')) || 
        (modelStr.startsWith('SM-') && cleanedProduct.includes(modelStr + '/DSN')) ||
        (modelStr.startsWith('SM-') && modelStr !== withoutDsORDsn && cleanedProduct.includes(withoutDsORDsn));
    
      if (modelExists) {
        if(modelStr.startsWith('SM-')){
          console.log('Model match:', model, cleanedProduct);
        }
        storageMap.forEach((data, storageKey) => {
          let storageMatches = false;

          if (ingramStorage && ingramStorage.originalMultiStorage && ingramStorage.originalMultiStorage.includes(' ')) {
            storageMatches = ingramStorage.sizes.every(size => {
              return cleanedProduct.includes(size.toString());
            });
          }
          else if (storageKey === 'NO_STORAGE') {
            storageMatches = true;
          } else if (ingramStorage || storageKey) {
            if (storageKey.includes(',')) {
              const requiredSizes = storageKey.split(',').map(size => parseInt(size.trim()));
              storageMatches = requiredSizes.every(size => {
                const sizeInName = cleanedProduct.includes(size.toString());
                return sizeInName;
              });
            }
            else if (ingramStorage && Array.isArray(ingramStorage.sizes)) {
              storageMatches = ingramStorage.sizes.includes(parseInt(storageKey));
            }
            else if (ingramStorage && ingramStorage.size !== undefined && ingramStorage.unit) {
              storageMatches = 
                (ingramStorage.unit === 'TB' && storageKey === `${ingramStorage.size}${ingramStorage.unit}`) ||
                (ingramStorage.unit === 'GB' && ingramStorage.size !== undefined && storageKey === ingramStorage.size.toString());
            } else {
              storageMatches = false;
            }
          } else {
            storageMatches = false;
          }

          if (storageMatches) {
            data.ingramIndices.add(index);
          }
        });
      }
    });
  });

  // Process model matches first
  modelMatches.forEach((storageMap, model) => {
    storageMap.forEach((data, storageKey) => {
      if (data.ingramIndices.size === 0) return;

      const gradeCounts = { BN: 0, GB: 0, GA: 0, OB: 0, LN: 0 };
      let totalQuantity = 0;

      // Collect all products for matching and quantity calculation
      const allProducts = [];

      data.products.forEach(product => {
        const grade = extractGrade(product.Name);
        if (grade) {
          const quantity = parseInt(product.Qty) || 0;
          gradeCounts[grade] += quantity;
          totalQuantity += quantity;
          allProducts.push(product);
        }
      });

      // Filter to only Grade A and Grade B products for avg price calculation
      const eligibleProducts = allProducts.filter(product => {
        const grade = extractGrade(product.Name);
        return grade === 'GA' || grade === 'GB';
      });

      let totalPrice = 0;
      let priceCount = 0;

      eligibleProducts.forEach(product => {
        // Calculate price (remove A$ and parse)
        if (product.Price) {
          const priceStr = String(product.Price).replace(/[A$,\s]/g, '');
          const price = parseFloat(priceStr);
          if (!isNaN(price) && price > 0) {
            totalPrice += price;
            priceCount++;
          }
        }
      });

      // Calculate OUR AVG PRICE (ex-GST) - only from GA and GB products
      const ourAvgPrice = priceCount > 0 ? (totalPrice / priceCount / 1.1) : 0;

      const baseName = getBaseProductName(eligibleProducts.length > 0 ? eligibleProducts[0].Name : data.products[0].Name);
      const storage = extractStorage(eligibleProducts.length > 0 ? eligibleProducts[0].Name : data.products[0].Name);
      const storageStr = storage ? ` (${storage.size}${storage.unit})` : '';

      const matchingIngramProducts = Array.from(data.ingramIndices).map(index => ({
        original: ingramData[index].Product,
        cleaned: cleanIngramProduct(ingramData[index].Product)
      }));

      // Build gradeCounts string for all grades
      const gradeCountParts = [];
      if (gradeCounts.GA > 0) gradeCountParts.push(`GA:${gradeCounts.GA}`);
      if (gradeCounts.GB > 0) gradeCountParts.push(`GB:${gradeCounts.GB}`);
      if (gradeCounts.OB > 0) gradeCountParts.push(`OB:${gradeCounts.OB}`);
      if (gradeCounts.LN > 0) gradeCountParts.push(`LN:${gradeCounts.LN}`);
      if (gradeCounts.BN > 0) gradeCountParts.push(`BN:${gradeCounts.BN}`);

      results.push({
        model,
        productName: `${baseName}${storageStr} (${model})`,
        totalQuantity, // GA+GB only (for compatibility)
        matchType: 'model',
        storage: storage ? `${storage.size}${storage.unit}` : 'N/A',
        gradeCounts: gradeCountParts.join(','),
        matchingProducts: [
          'OC Products:',
          ...allProducts.map(p => p.Name), // All products (GA, GB, OB, LN, BN)
          '',
          'Matching Ingram Products:',
          ...matchingIngramProducts.map(p => p.original)
        ].join('\n'),
        ingramIndices: Array.from(data.ingramIndices),
        ourAvgPrice: ourAvgPrice.toFixed(2)
      });

      // Mark these Ingram products as processed
      data.ingramIndices.forEach(idx => processedIngramIndices.add(idx));
    });
  });

  // Now do name matching for unmatched products
  normalizedOcProducts.forEach((productInfo, normalizedName) => {
    ingramData.forEach((ingramProduct, ingramIndex) => {
      if (processedIngramIndices.has(ingramIndex)) return;

      const { normalized: normalizedIngramName, storage: ingramStorage } = normalizeProductName(ingramProduct.Product);
      
      // Get all possible variations of the name and make comparison case-insensitive
      const variations = getNameVariations(normalizedName);
      const normalizedIngramLower = normalizedIngramName.toLowerCase();
      const nameMatch = variations.some(variant =>{
        
        return normalizedIngramLower.startsWith(variant.toLowerCase())
      }
      );

      if (nameMatch) {
        const storageMatches = !productInfo.storage || !ingramStorage || 
          (productInfo.storage.isMultiple && productInfo.storage.sizes.every(size => 
            ingramStorage.isMultiple ? ingramStorage.sizes.includes(size) : ingramStorage.size === size
          )) ||
          (!productInfo.storage.isMultiple && !ingramStorage.isMultiple && productInfo.storage.size === ingramStorage.size);

        if (storageMatches) {
          if (!nameMatches.has(normalizedName)) {
            nameMatches.set(normalizedName, {
              productInfo,
              ingramIndices: new Set()
            });
          }
          
          nameMatches.get(normalizedName).ingramIndices.add(ingramIndex);
          processedIngramIndices.add(ingramIndex);
        }
      }
    });
  });

  // Process name matches
  nameMatches.forEach((match, normalizedName) => {
    const productInfo = match.productInfo;
    
    Array.from(match.ingramIndices).forEach(ingramIndex => {
      // Use all products for quantity calculation (but filter for price calculation)
      const allProducts = productInfo.originalProducts;
      let totalQuantity = 0;

      // Calculate total quantity across ALL grades
      Object.values(productInfo.quantities).forEach(qty => {
        totalQuantity += qty;
      });

      // Filter to only Grade A and Grade B products for avg price calculation
      const eligibleProducts = allProducts.filter(p => {
        const grade = extractGrade(p.Name);
        return grade === 'GA' || grade === 'GB';
      });

      let totalPrice = 0;
      let priceCount = 0;

      eligibleProducts.forEach(product => {
        // Calculate price
        if (product.Price) {
          const priceStr = String(product.Price).replace(/[A$,\s]/g, '');
          const price = parseFloat(priceStr);
          if (!isNaN(price) && price > 0) {
            totalPrice += price;
            priceCount++;
          }
        }
      });

      // Calculate OUR AVG PRICE (ex-GST) - only from GA and GB products
      const ourAvgPrice = priceCount > 0 ? (totalPrice / priceCount / 1.1) : 0;

      // Build gradeCounts string for all grades
      const gradeCountParts = [];
      if (productInfo.quantities.GA > 0) gradeCountParts.push(`GA:${productInfo.quantities.GA}`);
      if (productInfo.quantities.GB > 0) gradeCountParts.push(`GB:${productInfo.quantities.GB}`);
      if (productInfo.quantities.OB > 0) gradeCountParts.push(`OB:${productInfo.quantities.OB}`);
      if (productInfo.quantities.LN > 0) gradeCountParts.push(`LN:${productInfo.quantities.LN}`);
      if (productInfo.quantities.BN > 0) gradeCountParts.push(`BN:${productInfo.quantities.BN}`);

      results.push({
        model: normalizedName,
        productName: ingramData[ingramIndex].Product,
        totalQuantity, // GA+GB only (for compatibility)
        matchType: 'name',
        storage: extractStorage(ingramData[ingramIndex].Product)?.size || 'N/A',
        gradeCounts: gradeCountParts.join(','),
        matchingProducts: allProducts.map(p => p.Name).join('\n'),
        ingramIndices: [ingramIndex],
        ourAvgPrice: ourAvgPrice.toFixed(2)
      });
    });
  });

  return results;
};

const LogicComponent = () => {
  const [ocProductData, setOcProductData] = useState([]);
  const [ingramData, setIngramData] = useState([]);
  const [auctionData, setAuctionData] = useState([]);
  const [result, setResult] = useState([]);
  const [error, setError] = useState('');
  
  // New AI-related state
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState('');

  const handleFileUpload = (file, type) => {
    if (!file) return;

    if (type === 'Phonebot Stock Xlsx') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          setOcProductData(jsonData);
          setError('');
        } catch (error) {
          console.error('Error processing XLSX:', error);
          setError('Error processing OC Products file');
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (type === 'Ingram xlsx') {
      const reader = new FileReader();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      reader.onload = (e) => {
        try {
          let jsonData;
          
          if (fileExtension === 'xlsx') {
            // Handle XLSX file
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          } else if (fileExtension === 'csv') {
            // Handle CSV file
            const text = e.target.result;
            const result = Papa.parse(text, { header: true });
            jsonData = result.data;
          }
          
          setIngramData(jsonData);
          setError('');
        } catch (error) {
          console.error('Error processing file:', error);
          setError('Error processing Ingram Micro file');
        }
      };
      
      if (fileExtension === 'xlsx') {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    } else if (type === 'Auction xlsx') {
      const reader = new FileReader();
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      reader.onload = (e) => {
        try {
          let jsonData;
          
          if (fileExtension === 'xlsx') {
            // Handle XLSX file
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            jsonData = XLSX.utils.sheet_to_json(worksheet);
          } else if (fileExtension === 'csv') {
            // Handle CSV file
            const text = e.target.result;
            const result = Papa.parse(text, { header: true });
            jsonData = result.data;
          }
          
          setAuctionData(jsonData);
          setError('');
        } catch (error) {
          console.error('Error processing file:', error);
          setError('Error processing Auction file');
        }
      };
      
      if (fileExtension === 'xlsx') {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    }
  };

  const processData = () => {
    if (!ocProductData.length || !ingramData.length) {
      setError('Please upload both required files first');
      return;
    }

    try {
      const results = processModelMatches(ocProductData, ingramData);
      setResult(results);
      setError('');
      setAiAnalysisResult(null); // Clear previous AI results
    } catch (error) {
      console.error('Error processing data:', error);
      setError('Error processing data');
    }
  };

  // NEW AI ANALYSIS FUNCTION
  const runAIAnalysis = async () => {
    if (!result.length) {
      setError('Please process files first before running AI analysis');
      return;
    }

    setAiLoading(true);
    setAiProgress('🚀 Starting AI analysis...');
    setError('');

    try {
      // Prepare data in the format expected by our API
      const requestData = {
        phonebot_data: ocProductData,
        ingram_data: ingramData,
        matched_products: result.map((item, index) => {
          // Validate ingramIndices before using
          const ingramIndices = item.ingramIndices || [];
          if (!Array.isArray(ingramIndices) || ingramIndices.length === 0) {
            console.error('Invalid ingramIndices for matched product:', {
              productName: item.productName,
              ingramIndices: item.ingramIndices,
              index: index
            });
            return null; // Skip this item
          }

          // Ensure ingram_index is the first valid index, not array position
          const primaryIndex = ingramIndices[0];

          // Debug: Log potential issues
          if (primaryIndex !== index) {
            console.log('✅ Correct ingram_index mapping:', {
              productName: item.productName,
              primaryIndex: primaryIndex,
              arrayIndex: index,
              ingramIndices: ingramIndices
            });
          } else {
            console.warn('⚠️  Potential fallback to array index:', {
              productName: item.productName,
              primaryIndex: primaryIndex,
              arrayIndex: index,
              ingramIndices: ingramIndices
            });
          }

          return {
            ingram_index: primaryIndex,
            ingram_indices: ingramIndices,
            ingram_product: extractStandardizedNameFromMatchedProducts(item.matchingProducts),
          store_products: item.matchingProducts.split('\n')
            .filter(line => !line.includes('OC Products:') && !line.includes('Matching Ingram Products:') && line.trim())
            .map(name => ({ Name: name.trim() })),
          match_type: item.matchType,
          total_quantity: item.totalQuantity,
          storage: item.storage,
          grade_counts: parseGradeCounts(item.gradeCounts),
          priority_score: calculatePriorityScore(item),
          our_avg_price: parseFloat(item.ourAvgPrice) || 0
          };
        }).filter(item => item !== null), // Remove null items
        processing_stats: {
          phonebot_products: ocProductData.length,
          ingram_products: ingramData.length,
          total_matches: result.length,
          match_rate: result.length / ingramData.length
        }
      };

      setAiProgress('🔍 Researching webuyback prices...');
      
      // Call our AI backend API
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? '/api/process-with-ai' 
        : 'http://localhost:8000/api/process-with-ai';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      setAiProgress('🛒 Researching Australian market prices...');
      
      const aiResult = await response.json();

      if (aiResult.success) {
        setAiAnalysisResult(aiResult);
        setAiProgress('✅ AI analysis completed successfully!');
        console.log('AI Analysis Summary:', aiResult.analysis_summary);
      } else {
        throw new Error(aiResult.error || 'AI analysis failed');
      }

    } catch (error) {
      console.error('AI Analysis Error:', error);
      setError(`AI analysis failed: ${error.message}`);
      setAiProgress('❌ AI analysis failed');
    } finally {
      setAiLoading(false);
    }
  };

  // Helper function to parse grade counts
  const parseGradeCounts = (gradeCountsString) => {
    const counts = { BN: 0, GA: 0, GB: 0, OB: 0, LN: 0 };
    if (!gradeCountsString) return counts;

    const parts = gradeCountsString.split(',');
    parts.forEach(part => {
      const [grade, count] = part.split(':');
      if (grade && count && counts.hasOwnProperty(grade)) {
        counts[grade] = parseInt(count) || 0;
      }
    });

    return counts;
  };

  // Helper function to calculate priority score
  const calculatePriorityScore = (item) => {
    let score = 0;

    // Low stock gets high priority (use total stock across all grades)
    // Calculate total stock from grade_counts
    const gradeCounts = parseGradeCounts(item.gradeCounts);
    const totalStock = Object.values(gradeCounts).reduce((sum, qty) => sum + qty, 0);

    if (totalStock <= 5) score += 10;
    else if (totalStock <= 10) score += 5;
    
    // Model matches are more reliable
    if (item.matchType === 'model') score += 3;
    else if (item.matchType === 'name') score += 1;
    
    // Apple products get slight boost
    if (item.productName.toLowerCase().includes('apple') || 
        item.productName.toLowerCase().includes('iphone') ||
        item.productName.toLowerCase().includes('ipad')) {
      score += 2;
    }
    
    return score;
  };

  // ENHANCED EXPORT FUNCTION (includes AI data if available)
  const handleExport = () => {
    try {
      let dataToExport;
      let filename;

      if (aiAnalysisResult && aiAnalysisResult.enhanced_data) {
        // Export AI-enhanced data
        dataToExport = aiAnalysisResult.enhanced_data;
        filename = 'ai_enhanced_ingram_products.xlsx';
        console.log('Exporting AI-enhanced data...');
      } else {
        // Export regular enhanced data (original logic)
        const ingramMatchMap = new Map();
        result.forEach(item => {
          item.ingramIndices.forEach(index => {
            if (!ingramMatchMap.has(index)) {
              ingramMatchMap.set(index, {
                quantity: item.totalQuantity,
                gradeCount: item.gradeCounts,
                matchedProducts: item.matchingProducts.split('\n')
                  .filter(line => !line.includes('OC Products:') && !line.includes('Matching Ingram Products:') && line.trim())
                  .join(' | '),
                matchType: item.matchType,
                ourAvgPrice: item.ourAvgPrice || ''
              });
            }
          });
        });

        // Create enhanced Ingram data with store product matching
        dataToExport = ingramData.map((row, index) => {
          const match = ingramMatchMap.get(index);
          if (match && match.matchType && match.matchType.trim() !== '') {
            return {
              ...row,
              quantity: match.quantity,
              gradeCount: match.gradeCount,
              matchedProducts: match.matchedProducts,
              matchType: match.matchType,
              ourAvgPrice: match.ourAvgPrice
            };
          }
          return {
            ...row,
            quantity: '',
            gradeCount: '',
            matchedProducts: '',
            matchType: '',
            ourAvgPrice: ''
          };
        });

        filename = 'enhanced_ingram_products.xlsx';
      }

      // NEW: Direct Product-to-Product matching for auction data (runs for both AI and regular exports)
      if (auctionData.length > 0 && dataToExport) {
        console.log("🚀 Processing auction data with direct product matching...");
        console.log(`📊 Export type: ${filename}`);
        console.log(`📊 Data to export length: ${dataToExport.length}`);
        console.log(`📊 Auction data length: ${auctionData.length}`);

        // Create a simple map: Product Name → Offer Value
        const auctionOfferMap = new Map();

        // Debug auction data structure
        console.log(`📊 Auction data sample:`, auctionData.slice(0, 2));
        console.log(`📊 Auction data columns:`, Object.keys(auctionData[0] || {}));

        auctionData.forEach(auctionRow => {
          // Try multiple possible column names for product and offer
          const productName = auctionRow.Product ||
                             auctionRow['Product Name'] ||
                             auctionRow['Product'] ||
                             auctionRow.product ||
                             '';

          const offerValue = auctionRow.Offer ||
                            auctionRow[' Offer '] ||
                            auctionRow['Best Offer'] ||
                            auctionRow.offer ||
                            '';

          console.log(`📊 Processing auction row - Product: "${productName}", Offer: "${offerValue}"`);

          if (productName && offerValue) {
            auctionOfferMap.set(productName.trim(), offerValue);
          }
        });

        console.log(`📊 Created auction offer map with ${auctionOfferMap.size} entries`);
        console.log(`📊 Sample auction products:`, Array.from(auctionOfferMap.keys()).slice(0, 3));
        console.log(`📊 Sample export products:`, dataToExport.slice(0, 3).map(row => ({
          Product: row.Product,
          'Offer': row.Offer,
          ' Offer ': row[' Offer ']
        })));

        // Direct product matching between export data and Auction sheets
        let directMatchCount = 0;

        dataToExport.forEach((row, index) => {
          const productName = (row.Product || '').trim();

          if (!productName) return;

          // Look for direct product name match in auction data (exact matching)
          const auctionOffer = auctionOfferMap.get(productName);

          if (auctionOffer) {
            // Copy the offer value to appropriate column
            if (typeof row[' Offer '] !== 'undefined') {
              row[' Offer '] = auctionOffer;
            } else if (typeof row['Offer'] !== 'undefined') {
              row['Offer'] = auctionOffer;
            } else {
              // If neither column exists, add it
              row['Offer'] = auctionOffer;
            }

            directMatchCount++;
            console.log(`✅ Direct match [${index}]: "${productName}" → Offer: ${auctionOffer}`);
          } else {
            // Debug: Check if this product exists in auction data with slight variations
            const similarProducts = Array.from(auctionOfferMap.keys()).filter(auctionProduct =>
              auctionProduct.toLowerCase().includes(productName.toLowerCase().slice(0, 10)) ||
              productName.toLowerCase().includes(auctionProduct.toLowerCase().slice(0, 10))
            );
            if (similarProducts.length > 0) {
              console.log(`❌ No exact match [${index}]: "${productName}" - Similar in auction: ${similarProducts.slice(0, 2)}`);
            } else {
              console.log(`❌ No match [${index}]: "${productName}"`);
            }
          }
        });

        console.log(`Total direct matches found: ${directMatchCount}`);
      }

      // Create Excel file
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      
      // Add red background to unmatched rows
      const redFill = { fgColor: { rgb: "FFFF0000" } };
      
      dataToExport.forEach((row, idx) => {
        if (!row.matchType || row.matchType.trim() === '') {
          const range = XLSX.utils.decode_range(ws['!ref']);
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: idx + 1, c: col });
            if (!ws[cellRef]) ws[cellRef] = { v: '' };
            ws[cellRef].s = { fill: redFill };
          }
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Enhanced Products');

      XLSX.writeFile(wb, filename);

      console.log(`✅ Exported: ${filename}`);

    } catch (error) {
      console.error('Error exporting XLSX:', error);
      setError('Error exporting XLSX file');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>🤖 AI-Enhanced Ingram Product Matcher</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3>Upload Files</h3>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div>
            <label>Phonebot Stock Xlsx:</label>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => handleFileUpload(e.target.files[0], 'Phonebot Stock Xlsx')}
              style={{ display: 'block', marginTop: '5px' }}
            />
          </div>
          <div>
            <label>Ingram Micro (XLSX):</label>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => handleFileUpload(e.target.files[0], 'Ingram xlsx')}
              style={{ display: 'block', marginTop: '5px' }}
            />
          </div>
          <div>
            <label>Last Week Auction (Optional):</label>
            <input
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => handleFileUpload(e.target.files[0], 'Auction xlsx')}
              style={{ display: 'block', marginTop: '5px' }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            onClick={processData}
            disabled={!ocProductData.length || !ingramData.length}
            style={{ 
              padding: '10px 20px',
              backgroundColor: (!ocProductData.length || !ingramData.length) ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (!ocProductData.length || !ingramData.length) ? 'not-allowed' : 'pointer'
            }}
          >
            1️⃣ Process Files
          </button>

          <button 
            onClick={runAIAnalysis}
            disabled={!result.length || aiLoading}
            style={{ 
              padding: '10px 20px',
              backgroundColor: !result.length ? '#ccc' : aiLoading ? '#ffc107' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !result.length ? 'not-allowed' : 'pointer',
              position: 'relative'
            }}
          >
            {aiLoading ? '⏳ Analyzing...' : '🤖 2️⃣ AI Analysis'}
          </button>

          <button 
            onClick={handleExport}
            disabled={!result.length}
            style={{ 
              padding: '10px 20px',
              backgroundColor: !result.length ? '#ccc' : aiAnalysisResult ? '#6f42c1' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: !result.length ? 'not-allowed' : 'pointer'
            }}
          >
            {aiAnalysisResult ? '📊 3️⃣ Export AI Enhanced' : '📄 Export Basic'}
          </button>
        </div>
      </div>

      {/* AI Progress Display */}
      {aiLoading && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#e3f2fd', 
          border: '1px solid #2196f3', 
          borderRadius: '4px', 
          marginBottom: '20px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              border: '2px solid #2196f3', 
              borderTopColor: 'transparent', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite' 
            }}></div>
            <span>{aiProgress}</span>
          </div>
        </div>
      )}

      {/* AI Results Summary */}
      {aiAnalysisResult && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#e8f5e8', 
          border: '1px solid #4caf50', 
          borderRadius: '4px', 
          marginBottom: '20px' 
        }}>
          <h4>🎯 AI Analysis Results</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div>
              <strong>Processing Time:</strong> {aiAnalysisResult.analysis_summary.processing_time_seconds?.toFixed(1)}s
            </div>
            <div>
              <strong>Products Analyzed:</strong> {aiAnalysisResult.analysis_summary.products_analyzed}
            </div>
            <div>
              <strong>WebuyBack Success:</strong> {(aiAnalysisResult.analysis_summary.webuyback_research?.success_rate * 100)?.toFixed(0)}%
            </div>
            <div>
              <strong>Market Research Success:</strong> {(aiAnalysisResult.analysis_summary.market_research?.success_rate * 100)?.toFixed(0)}%
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <strong>📋 Recommendations:</strong>
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              {aiAnalysisResult.analysis_summary.recommendations?.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: 'red', marginBottom: '20px', padding: '10px', backgroundColor: '#ffeaa7', borderRadius: '4px' }}>
          ⚠️ {error}
        </div>
      )}

      {result.length > 0 && (
        <div>
          <h3>📊 Processing Results ({result.length} matches)</h3>
          <div style={{ 
            maxHeight: '400px', 
            overflowY: 'auto', 
            border: '1px solid #ddd', 
            borderRadius: '4px' 
          }}>
            {result.map((item, index) => (
              <div key={index} style={{ 
                marginBottom: '15px', 
                padding: '15px', 
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: (() => {
                  const gradeCounts = parseGradeCounts(item.gradeCounts);
                  const totalStock = Object.values(gradeCounts).reduce((sum, qty) => sum + qty, 0);
                  return totalStock <= 5 ? '#fff3cd' : 'white';
                })()
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                  <div><strong>Product:</strong> {item.productName}</div>
                  <div><strong>Stock:</strong> {(() => {
                    const gradeCounts = parseGradeCounts(item.gradeCounts);
                    const totalStock = Object.values(gradeCounts).reduce((sum, qty) => sum + qty, 0);
                    return totalStock;
                  })()} {(() => {
                    const gradeCounts = parseGradeCounts(item.gradeCounts);
                    const totalStock = Object.values(gradeCounts).reduce((sum, qty) => sum + qty, 0);
                    return totalStock <= 5 ? '🔴' : '🟢';
                  })()}</div>
                  <div><strong>Match:</strong> {item.matchType}</div>
                  <div><strong>Storage:</strong> {item.storage}</div>
                </div>
                <div style={{ marginTop: '5px' }}>
                  <strong>Grades:</strong> {item.gradeCounts}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add CSS for spinner animation */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LogicComponent;
